import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { llmApi } from "@/lib/api/llm";
import {
  LLMProvider,
  LLMProviderCreate,
  LLMProviderUpdate,
  LLMProviderDetail,
  LLMUsageStats,
  TestConnectionResponse,
} from "@/types/llm";

// Query keys para React Query
const llmKeys = {
  all: ["llm"] as const,
  providers: () => [...llmKeys.all, "providers"] as const,
  provider: (id: string) => [...llmKeys.all, "provider", id] as const,
  usage: (period?: string) => [...llmKeys.all, "usage", period] as const,
};

/**
 * Hook para listar todos los proveedores LLM
 */
export function useLLMProviders() {
  return useQuery({
    queryKey: llmKeys.providers(),
    queryFn: llmApi.listProviders,
    staleTime: 30000, // 30 segundos
  });
}

/**
 * Hook para obtener detalle de un proveedor + estadísticas
 */
export function useLLMProvider(id: string) {
  return useQuery({
    queryKey: llmKeys.provider(id),
    queryFn: () => llmApi.getProvider(id),
    enabled: !!id,
    staleTime: 10000, // 10 segundos
  });
}

/**
 * Hook para crear un nuevo proveedor LLM
 */
export function useCreateLLMProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LLMProviderCreate) => llmApi.createProvider(data),
    onSuccess: () => {
      // Invalidar lista de providers para refrescar
      queryClient.invalidateQueries({ queryKey: llmKeys.providers() });
    },
  });
}

/**
 * Hook para actualizar un proveedor LLM
 */
export function useUpdateLLMProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LLMProviderUpdate }) =>
      llmApi.updateProvider(id, data),
    onSuccess: (_, variables) => {
      // Invalidar provider específico y lista
      queryClient.invalidateQueries({ queryKey: llmKeys.provider(variables.id) });
      queryClient.invalidateQueries({ queryKey: llmKeys.providers() });
    },
  });
}

/**
 * Hook para eliminar un proveedor LLM
 */
export function useDeleteLLMProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => llmApi.deleteProvider(id),
    onSuccess: () => {
      // Invalidar lista de providers
      queryClient.invalidateQueries({ queryKey: llmKeys.providers() });
    },
  });
}

/**
 * Hook para probar la conexión con un proveedor
 */
export function useTestLLMConnection() {
  return useMutation({
    mutationFn: (id: string) => llmApi.testConnection(id),
  });
}

/**
 * Hook para establecer un proveedor como default
 */
export function useSetDefaultProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => llmApi.setDefault(id),
    onSuccess: () => {
      // Invalidar lista de providers (porque cambió el default)
      queryClient.invalidateQueries({ queryKey: llmKeys.providers() });
    },
  });
}

/**
 * Hook para obtener estadísticas de uso de LLM
 * @param period - Período de tiempo (e.g., "7d", "30d", "all")
 * @param enabled - Si false, no dispara la query (p. ej. usuario sin rol
 *   platform_admin: el endpoint devolvería 403 y solo generaría ruido).
 */
export function useLLMUsageStats(period?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: llmKeys.usage(period),
    queryFn: () => llmApi.getUsageStats(period),
    staleTime: 60000, // 1 minuto
    enabled,
  });
}
